import { asBlob } from 'html-docx-js-typescript';
import { DownloadChatSession } from '../../services/providers/progpt/dtos/responses/ProGPTDownloadChatSessionDto';
import { ChatMessage } from '../../domain/messages/models/ChatMessage';


const sanitizeFilename = (value: string): string => value.replace(/[\\/:*?"<>|]/g, '_').trim();

const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
        return '';
    }

    return date.toLocaleString();
};

const formatMessageContent = (content: string): string => {
    const escapedContent = content.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return escapedContent
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/```([\s\S]*?)```/g, '<code>$1</code>');
};

export const downloadChatSessionAsText = (sessionData: DownloadChatSession, fallbackTitle: string): void => {
    const chatTitle = sessionData.chatTitle || fallbackTitle || 'Chat Session';
    const safeFileName = sanitizeFilename(chatTitle) || 'chat-session';

    const lines = sessionData.chatHistory
        .filter((m) => m.content?.trim())
        .map((m) => {
            const time = formatTimestamp(m.timestamp);
            const role = m.userName || (m.authorRole === 1 ? 'Assistant' : 'User');
            return `[${time}] ${role}:\n${m.content}\n`;
        });

    const text = `${chatTitle}\n${'='.repeat(chatTitle.length)}\n\n${lines.join('\n')}`;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = `${safeFileName}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
};

export const downloadChatSessionDocumentAsync = async (
    sessionData: DownloadChatSession,
    fallbackTitle: string,
): Promise<void> => {
    const chatTitle = sessionData.chatTitle || fallbackTitle || 'Chat Session';
    const safeFileName = sanitizeFilename(chatTitle) || 'chat-session';

    const preHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8" />
    <title>Chat Session</title>
    <style>
        body { font-family: Arial, sans-serif; font-size: 10pt; line-height: 14pt; }
        h2 { margin-bottom: 16px; }
        .timestamp { font-size: 10pt; color: #6b6b6b; }
        .content { font-family: Arial, sans-serif; font-size: 10pt; }
        code { background: #f5f5f5; padding: 2px 4px; }
    </style>
</head>
<body>
`;
    const postHtml = '</body></html>';

    let innerHtml = `<h2>${chatTitle}</h2>`;

    sessionData.chatHistory.forEach((message: ChatMessage) => {
        if (message.userName === 'Bot' && (!message.content || message.content.trim() === '')) {
            return;
        }

        let content = formatMessageContent(message.content || '');
        if (message.images && message.images.length > 0) {
            content += message.userName === 'Bot' ? '[Image included in response]' : '[Image uploaded]';
        }

        innerHtml += `
        <div class="message">
            <div class="header">
                <h4>${message.userName} <span class="timestamp">${formatTimestamp(message.timestamp)}</span></h4>
            </div>
            <div class="content"><pre>${content}</pre></div>
            <hr />
        </div>
        `;
    });

    const html = preHtml + innerHtml + postHtml;
    const converted = await asBlob(html, {
        orientation: 'portrait',
        margins: { top: 720, right: 720, bottom: 720, left: 720 },
    });

    const blob =
        converted instanceof Blob
            ? converted
            : new Blob([converted as BlobPart], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            });

    const blobUrl = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = blobUrl;
    downloadLink.download = `${safeFileName}.docx`;

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(blobUrl);
};
